import * as React from "react";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useModal } from "../../../../utils/custom-hooks";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import {
  SurveyContextProps,
  SurveyDetailService,
} from "../../../../application/service/service-provider/survey-detail-service";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderR } from "../../service-provider/routes";
import { SurveysServiceProviderR } from "./route";
import {
  reportUnexpectedError,
  connectPageService,
  SurveyStatusTag,
  SurveyTabState,
} from "@graffer-inc/smart-apply";
import { Stats } from "../survey-detail/tabs/stats";
import { FormSetting } from "../survey-detail/tabs/form-setting";
import { Loader } from "../../../organisms/common/loading";
import { RBACButton } from "../../../molecules/rbac-button";
import { ConfirmModal } from "../../../organisms/common/confirm-modal";

import { toast } from "react-toastify";
import { Tabs, WarningBanner } from "@graffer-inc/console-design-kit";
import apiClient from "../../../../application/apis/api-client";

const SurveyDetailPage: React.FC<SurveyContextProps> = ({
  isSubmitting,
  tabState,
  survey,
  groups,
  switchTab,
  resetState,
  onExportSurvey,
}) => {
  const { id: tenantId, surveyId: id } = useParams<{
    id: string;
    surveyId: string;
  }>();
  const { tab } = parseQueryOfLocation(useLocation());
  const { id: orgId } = useParams<{ id: string }>();
  const [slug, setSlug] = React.useState("");
  const fetchOrganization = React.useCallback(async () => {
    try {
      const { data: organization } = await apiClient.getOrganization(orgId);
      setSlug(organization.slug);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("テナント情報の取得に失敗しました。");
    }
  }, [orgId, setSlug]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const exportSurveyModal = useModal();

  const onDownloadSurvey = async () => {
    await onExportSurvey(survey!.id);
    exportSurveyModal.close();
  };

  useEffect(() => {
    resetState(id, tab as string | undefined, { orgIdFromPath: orgId });
  }, [resetState, id, tab, orgId]);

  if (!survey) {
    return <WarningBanner message="アンケートの取得に失敗しました。" />;
  }

  return (
    <ServiceProviderTemplate
      breadcrumbs={[
        { to: ServiceProviderR.Home.link({}), title: "テナント一覧" },
        {
          to: SurveysServiceProviderR.Surveys.link({ id: tenantId }),
          title: "アンケート一覧",
        },
        {
          to: SurveysServiceProviderR.SurveyDetail.link({
            tenantId,
            surveyId: id,
          }),
          title: id,
        },
      ]}
    >
      <div className="columns is-vcentered" style={{ marginBottom: "0px" }}>
        <div className="column has-text-left">
          <h1 className="title">
            <i className="fa fa-poll" /> {survey?.name}
          </h1>
        </div>
        <div className={"column has-text-right is-narrow"}>
          <RBACButton
            target={survey}
            requirePermissions={"procedures.update"}
            onClick={exportSurveyModal.open}
            buttonTypes={"secondary"}
            buttonText={"アンケートをエクスポートする"}
          />
        </div>
      </div>
      <div style={{ margin: "0 0 10px 0" }}>
        <SurveyStatusTag surveyStatus={survey.publishStatus} />
      </div>
      <div className="columns">
        <div className="column">
          <Tabs
            tabItems={[
              {
                label: "基本情報",
                isActive: tabState.equals("STATS"),
                onClick: () => switchTab(SurveyTabState.initialize()),
                hidden: false,
              },
              {
                label: "フォーム設定",
                isActive: tabState.equals("FORM"),
                onClick: () => switchTab(SurveyTabState.form()),
                hidden: false,
              },
            ]}
          />
        </div>
      </div>
      <div className="columns">
        <div className="column">
          <Loader isLoading={isSubmitting}>
            {tabState.matchType({
              STATS: survey && (
                <Stats
                  survey={survey!}
                  onSurveyUpdate={() => {
                    return;
                  }}
                  orgSlug={slug}
                  canUpdateSurvey={false}
                  canReadStats={false}
                  groups={groups}
                />
              ),
              FORM: (
                <FormSetting
                  disabled={true}
                  survey={survey!}
                  onSurveyUpdate={() => {
                    return;
                  }}
                  canUpdateSurvey={false}
                  // ServiceProviderからはアンケートのフォームは更新できないので
                  // フォームの保存状態を管理する必要がない
                  setIsFormUnsaved={() => undefined}
                />
              ),
              RESULTS: <></>,
            })}
          </Loader>
        </div>
      </div>
      <ConfirmModal
        isActive={exportSurveyModal.isActive}
        title="アンケートをエクスポート"
        body="このアンケートの設定をJSONファイルとしてエクスポートします。"
        successButtonText="エクスポート"
        onSuccess={onDownloadSurvey}
        onClose={exportSurveyModal.close}
      />
    </ServiceProviderTemplate>
  );
};

export const OrganizationSurveyDetailContainer = connectPageService(
  SurveyDetailPage,
  () => new SurveyDetailService(),
);
